import { KojvService, ObjectService, SelfServiceObjectServicesResponseDto } from '@ekt-group/general-purpose-api-interfaces';
import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT } from '@/constants';
import { formatAsCurrency } from '@/helpers/number-formatter';

import { useGetObjectServices } from '../api/use-get-object-services';

type Props = {
  objectId: number;
  isModalOpen: boolean;
  handleCancel: () => void;
};

const ObjectServicesModal = ({ isModalOpen, handleCancel, objectId }: Props) => {
  const { t } = useTranslation('objectsTable');

  const objectServicesQuery = useGetObjectServices({
    params: {
      objectIds: [objectId],
    },
    queryConfig: {
      enabled: !!objectId,
    },
  });

  const getLatestPeriod = (service: SelfServiceObjectServicesResponseDto) => {
    if (!service?.periods?.length) {
      return null;
    }

    return [...service.periods].sort((a, b) => dayjs(b.startDate).diff(dayjs(a.startDate))).find((period) => period.objectServices?.length);
  };

  const service = objectServicesQuery.data?.find((service) => service.objectId === objectId);

  const latestPeriod = getLatestPeriod(service);

  const activeObjectServices: ObjectService[] = latestPeriod?.objectServices ?? service?.objectServices ?? [];
  const activeKojvServices: KojvService[] = latestPeriod?.kojvServices ?? service?.kojvServices ?? [];

  const objectServices = activeObjectServices.map((objectService) => ({
    id: objectService.id,
    name: activeKojvServices.find((kojvService) => kojvService.id === objectService.kojvServiceId)?.name,
    isPerDischarge: objectService.isPerDischarge,
    priceWithVat: objectService.priceWithVat,
    startDate: objectService.startDate,
    endDate: objectService.endDate,
  }));

  const columns: ColumnsType<{
    id: number;
    name: string;
    isPerDischarge: boolean;
    priceWithVat: string;
    startDate: string;
    endDate: string;
  }> = [
    {
      key: 1,
      title: t('serviceName'),
      dataIndex: 'name',
    },
    {
      key: 2,
      title: t('billingType'),
      dataIndex: 'isPerDischarge',
      render: (isPerDischarge) => <span>{isPerDischarge ? t('maintenanceFee') : t('monthlyPayment')}</span>,
    },
    {
      key: 3,
      title: t('price', { ns: 'invoicesPage' }),
      dataIndex: 'priceWithVat',
      render: (priceWithVat) => <span>{formatAsCurrency(Number(priceWithVat || 0))}</span>,
    },
    {
      key: 4,
      title: t('period', { ns: 'contractsTable' }),
      render: (_, { startDate, endDate }) => {
        const formattedStartDate = startDate ? dayjs(startDate).format(DATE_FORMAT) : null;
        const formattedEndDate = endDate ? dayjs(endDate).format(DATE_FORMAT) : null;

        if (formattedStartDate && formattedEndDate) {
          return `${formattedStartDate} - ${formattedEndDate}`;
        }

        return formattedStartDate || formattedEndDate || '---';
      },
    },
  ];

  return (
    <Modal
      styles={{
        body: {
          height: '70vh',
          overflowY: 'auto',
        },
      }}
      onCancel={handleCancel}
      cancelButtonProps={{
        hidden: true,
      }}
      okButtonProps={{
        hidden: true,
      }}
      title={t('extraServices')}
      open={isModalOpen}
    >
      <Table
        rowKey={(record) => record.id?.toString()}
        pagination={false}
        loading={objectServicesQuery.isLoading}
        columns={columns}
        dataSource={objectServices}
      />
    </Modal>
  );
};

export default ObjectServicesModal;
